import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  alpha,
  FormHelperText,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import { motion } from 'motion/react';
import { useForm, Controller } from 'react-hook-form';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import privateLoungeImage from '../../imports/private-lounge.webp';

const gold = '#C0202A';
const silver = '#C0C0C0';
const successGreen = '#3FB950';
const leadConnectorWebhookUrl = import.meta.env.VITE_GHL_WEBHOOK_URL
  ?? 'https://services.leadconnectorhq.com/hooks/FGRhtyto8KOs08clawZI/webhook-trigger/576d7128-2e64-44ea-9604-04b8e9ec23b2';
const shouldSendContactSubmit = import.meta.env.VITE_ENABLE_CONTACT_SUBMIT === 'true';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  serviceType: string;
  propertyType: string;
  preferredContact: string;
  message: string;
  company: string;
}

const serviceOptions = [
  'Auto & Marine Detailing',
  'Carpet Cleaning & Restoration',
  'Tile Cleaning & Restoration',
  'Upholstery & Leather Cleaning',
  'Comprehensive Package (Multiple Services)',
  'Luxury Maintenance Program',
  'Other / Not Sure',
];

const propertyTypeOptions = [
  'Personal Vehicle',
  'Luxury / Exotic Vehicle',
  'Marine Vessel',
  'Residential Home',
  'Condominium / Townhome',
  'Commercial Property',
  'Multiple Properties',
];

const contactPreferenceOptions = [
  'Phone Call',
  'Text Message',
  'Email',
  'No Preference',
];

const locationOptions = [
  'Tucson',
  'Marana',
  'Oro Valley',
  'Vail',
  'Sahuarita',
  'Green Valley',
  'Sierra Vista',
  'Other / Not Listed',
];

const formatPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  const area = digits.slice(0, 3);
  const prefix = digits.slice(3, 6);
  const line = digits.slice(6, 10);

  if (digits.length > 6) return `(${area}) ${prefix}-${line}`;
  if (digits.length > 3) return `(${area}) ${prefix}`;
  if (digits.length > 0) return `(${area}`;
  return '';
};

const contactInfo = [
  {
    icon: <PhoneIcon sx={{ color: gold, fontSize: '1.2rem' }} />,
    label: 'Phone',
    value: '(520) 325-0576',
    sub: 'Tucson, Arizona',
  },
  {
    icon: <EmailIcon sx={{ color: gold, fontSize: '1.2rem' }} />,
    label: 'Email',
    value: 'info@lindseyenterprisesaz.com',
    sub: 'Please allow 24-48 hours to respond.',
  },
  {
    icon: <LocationOnIcon sx={{ color: gold, fontSize: '1.2rem' }} />,
    label: 'Service Area',
    value: 'Tucson & Surrounding Areas',
    sub: 'Marana · Oro Valley · Vail · Sahuarita · Green Valley · Sierra Vista',
  },
  {
    icon: <AccessTimeIcon sx={{ color: gold, fontSize: '1.2rem' }} />,
    label: 'Hours',
    value: 'Monday - Friday',
    sub: 'Appointments available Monday - Friday',
  },
];

export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      location: '',
      serviceType: '',
      propertyType: '',
      preferredContact: '',
      message: '',
      company: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    setSubmitError('');

    if (data.company) {
      setSubmitted(true);
      setSnackOpen(true);
      reset();
      return;
    }

    const submittedAt = new Date().toISOString();
    const source = {
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      href: window.location.href,
      submittedAt,
      userAgent: window.navigator.userAgent,
    };
    const payload = {
      ...data,
      fullName: `${data.firstName} ${data.lastName}`.trim(),
      source,
      leadSource: 'Lindsey Enterprises website',
    };
    if (shouldSendContactSubmit) {
      const response = await fetch(leadConnectorWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`LeadConnector webhook returned ${response.status}`);
      }
    } else {
      console.info('Contact form submission simulated. Payload:', payload);
    }

    setSubmitted(true);
    setSnackOpen(true);
    reset();
  };

  return (
    <Box sx={{ background: '#080808', minHeight: '100vh' }}>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: 18, md: 22 },
          pb: { xs: 10, md: 14 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${privateLoungeImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.5)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(4,4,4,0.77) 0%, rgba(4,4,4,0.45) 100%)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
              Begin the Experience
            </Typography>
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: '2.5rem', md: '5rem' }, color: '#F2EDE4', mb: 4, lineHeight: 1.05 }}
            >
              Schedule Your Service
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: alpha('#F2EDE4', 0.6), maxWidth: 580, lineHeight: 2 }}
            >
              Complete the form below and a Lindsey Enterprises professional will be in touch to discuss your needs and arrange a time that suits you.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 14 } }} id="booking-form">
        <Grid container spacing={{ xs: 6, md: 12 }}>
          {/* Left — Contact Info */}
          <Grid size={{ xs: 12, md: 4 }}>
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
                Get In Touch
              </Typography>
              <Typography variant="h4" sx={{ color: '#F2EDE4', mb: 4, fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
                We Are At Your Service
              </Typography>
              <Typography variant="body1" sx={{ color: alpha('#F2EDE4', 0.55), mb: 6, lineHeight: 2 }}>
                All work is performed by officers and key principals of the company. When you contact us, you speak directly with the people responsible for delivering your service.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {contactInfo.map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', gap: 2.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        border: `1px solid ${alpha(gold, 0.25)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{ color: alpha('#F2EDE4', 0.35), display: 'block', mb: 0.5, fontSize: '0.58rem' }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#F2EDE4', fontWeight: 400, fontSize: '0.9rem', mb: 0.3 }}
                      >
                        {item.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: alpha('#F2EDE4', 0.4), fontSize: '0.78rem' }}>
                        {item.sub}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 6, borderColor: alpha(gold, 0.12) }} />

              <Box
                sx={{
                  p: 4,
                  border: `1px solid ${alpha(gold, 0.2)}`,
                  background: alpha(gold, 0.03),
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: gold, display: 'block', mb: 2 }}
                >
                  IICRC Certified
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#F2EDE4', 0.6), lineHeight: 1.8, fontSize: '0.82rem' }}>
                  International Institute of Inspection, Cleaning and Restoration — the gold standard for professional cleaning services. Every technician, certified and continuously trained.
                </Typography>
              </Box>
            </motion.div>
          </Grid>

          {/* Right — Form */}
          <Grid size={{ xs: 12, md: 8 }}>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
              {submitted ? (
                <Box
                  sx={{
                    p: { xs: 6, md: 10 },
                    border: `1px solid ${alpha(gold, 0.25)}`,
                    background: alpha(gold, 0.03),
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ color: successGreen, fontSize: '3.5rem' }} />
                  <Typography variant="h4" sx={{ color: '#F2EDE4', fontSize: '2rem' }}>
                    Thank You
                  </Typography>
                  <Typography variant="body1" sx={{ color: alpha('#F2EDE4', 0.6), maxWidth: 440, lineHeight: 2 }}>
                    Your request has been received. A Lindsey Enterprises professional will be in touch with you shortly to discuss your service needs.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setSubmitted(false)}
                    sx={{ mt: 2, py: 1.5, px: 5, fontSize: '0.65rem', letterSpacing: '0.2em' }}
                  >
                    Submit Another Request
                  </Button>
                </Box>
              ) : (
                <Box
                  component="form"
                  onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
                    void handleSubmit(onSubmit, () => {
                      setSubmitError('');
                    })(event).catch((error) => {
                      console.error('LeadConnector form submission failed:', error);
                      setSubmitError('We could not send your request. Please call or email us directly.');
                      setSnackOpen(true);
                    });
                  }}
                  sx={{
                    p: { xs: 4, md: 6 },
                    border: `1px solid ${alpha(gold, 0.12)}`,
                    background: alpha('#111111', 0.5),
                  }}
                >
                  <Controller
                    name="company"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Company"
                        tabIndex={-1}
                        autoComplete="off"
                        sx={{
                          position: 'absolute',
                          left: '-10000px',
                          width: 1,
                          height: 1,
                          overflow: 'hidden',
                        }}
                      />
                    )}
                  />

                  <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 4 }}>
                    Service Request
                  </Typography>
                  <Box
                    sx={{
                      mb: 4,
                      p: 3,
                      border: `1px solid ${alpha(gold, 0.18)}`,
                      background: alpha(gold, 0.04),
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: alpha('#F2EDE4', 0.68), lineHeight: 1.8, fontSize: '0.82rem' }}
                    >
                      Please allow 24-48 hours to respond.
                    </Typography>
                  </Box>

                  {/* Name Row */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="firstName"
                        control={control}
                        rules={{ required: 'First name is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="First Name"
                            variant="outlined"
                            error={!!errors.firstName}
                            helperText={errors.firstName?.message}
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="lastName"
                        control={control}
                        rules={{ required: 'Last name is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Last Name"
                            variant="outlined"
                            error={!!errors.lastName}
                            helperText={errors.lastName?.message}
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Contact Row */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="email"
                        control={control}
                        rules={{
                          required: 'Email is required',
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Email Address"
                            type="email"
                            variant="outlined"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="phone"
                        control={control}
                        rules={{ required: 'Phone number is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            onChange={(event) => field.onChange(formatPhoneNumber(event.target.value))}
                            fullWidth
                            label="Phone Number"
                            type="tel"
                            inputProps={{ inputMode: 'tel' }}
                            variant="outlined"
                            error={!!errors.phone}
                            helperText={errors.phone?.message}
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Location */}
                  <Box sx={{ mb: 3 }}>
                    <Controller
                      name="location"
                      control={control}
                      rules={{ required: 'Please select your location' }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.location} size="small">
                          <InputLabel>Location</InputLabel>
                          <Select {...field} label="Location">
                            {locationOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                          {errors.location && (
                            <FormHelperText>{errors.location.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>

                  {/* Service Type */}
                  <Box sx={{ mb: 3 }}>
                    <Controller
                      name="serviceType"
                      control={control}
                      rules={{ required: 'Please select a service type' }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.serviceType} size="small">
                          <InputLabel>Service Type</InputLabel>
                          <Select {...field} label="Service Type">
                            {serviceOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                          {errors.serviceType && (
                            <FormHelperText>{errors.serviceType.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>

                  {/* Property Type */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="propertyType"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth size="small">
                            <InputLabel>Property / Vehicle Type</InputLabel>
                            <Select {...field} label="Property / Vehicle Type">
                              {propertyTypeOptions.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="preferredContact"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth size="small">
                            <InputLabel>Preferred Contact Method</InputLabel>
                            <Select {...field} label="Preferred Contact Method">
                              {contactPreferenceOptions.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Message */}
                  <Box sx={{ mb: 5 }}>
                    <Controller
                      name="message"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Tell Us About Your Project"
                          multiline
                          rows={5}
                          variant="outlined"
                          placeholder="Describe your vehicle, home, or specific concerns. Any details help us prepare for your service."
                        />
                      )}
                    />
                  </Box>

                  {/* Disclaimer */}
                  <Typography
                    variant="body2"
                    sx={{ color: alpha('#F2EDE4', 0.3), fontSize: '0.72rem', mb: 4, lineHeight: 1.7 }}
                  >
                    By submitting this form, you consent to being contacted by Lindsey Enterprises regarding your service request. We do not share your information with third parties.
                  </Typography>

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isSubmitting}
                    sx={{
                      py: 2.5,
                      fontSize: '0.72rem',
                      letterSpacing: '0.25em',
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </Box>
              )}
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackOpen}
        autoHideDuration={5000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={submitError ? 'error' : 'success'}
          sx={{
            background: '#111',
            color: '#F2EDE4',
            border: `1px solid ${alpha(gold, 0.4)}`,
            borderRadius: 0,
            '& .MuiAlert-icon': { color: submitError ? gold : successGreen },
          }}
        >
          {submitError || "Your request has been received. Please allow 24-48 hours to respond."}
        </Alert>
      </Snackbar>
    </Box>
  );
}
